import { useCallback, useEffect, useMemo, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Tabella } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'
import { GruppiRubrica } from './GruppiRubrica.tsx'
import { ComandiFileRubrica } from './FileRubrica.tsx'
import '../../stile-posta-caselle.css'

/**
 * Rubrica della posta (527, decisioni di Davide del 2/11/2026), una per casella.
 *
 * Si riempie da sola: ogni invio riuscito aggiunge i destinatari o ne conta un invio in piu', e lo
 * storico della posta inviata l'ha avviata. Qui si cerca, si corregge il nome, si toglie una voce e si
 * scrive a un contatto. Corregge e toglie chi scrive dalla casella; chi la legge soltanto la consulta.
 * I colleghi di CIDIS sono sempre proposti nel compositore e stanno qui sotto, senza essere copiati
 * nella rubrica.
 *
 * Dalla 539 (come in Roundcube): i gruppi di contatti (GruppiRubrica.tsx) e i file vCard e CSV da importare ed
 * esportare (FileRubrica.tsx), per chi corregge la rubrica.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })

const ORIGINI: Record<posta.Contatto['origine'], string> = {
  invio: 'dagli invii', storico: 'dallo storico', manuale: 'aggiunto a mano', importato: 'da un file',
}

/** Cerca dentro nome e indirizzo, senza distinguere maiuscole. */
function contiene(testo: string | null, q: string): boolean {
  return !!testo && testo.toLowerCase().includes(q)
}

export function Rubrica({ scrivi }: { scrivi: (casellaId: string, indirizzo: string) => void }) {
  const [caselle, setCaselle] = useState<posta.CasellaWebmail[] | null>(null)
  const [casellaId, setCasellaId] = useState('')
  const [rubrica, setRubrica] = useState<posta.Rubrica | null>(null)
  const [cerca, setCerca] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [correggi, setCorreggi] = useState<posta.Contatto | null>(null)
  const [togli, setTogli] = useState<posta.Contatto | null>(null)
  const [aggiungi, setAggiungi] = useState(false)

  useEffect(() => {
    posta.elencaCaselle().then((elenco) => {
      // In supervisione si legge la posta, non la rubrica: l'API risponderebbe 404.
      const usabili = elenco.filter((c) => !c.supervisione)
      setCaselle(usabili)
      setCasellaId((usabili.find((c) => c.mia && c.ambito === 'utente') ?? usabili[0])?.id ?? '')
    }).catch((e) => setErrore(posta.interruttoreSpento(e)
      ? 'La webmail non è attiva: la rubrica si usa con la posta del gestionale.' : testoErrore(e)))
  }, [])

  const carica = useCallback(() => {
    if (!casellaId) return
    posta.leggiRubrica(casellaId).then((r) => { setRubrica(r); setErrore(null) })
      .catch((e) => setErrore(testoErrore(e)))
  }, [casellaId])
  useEffect(() => { setRubrica(null); carica() }, [carica])

  const casella = caselle?.find((c) => c.id === casellaId) ?? null
  const gestisce = !!casella?.mia
  const q = cerca.trim().toLowerCase()
  const contatti = useMemo(() => (rubrica?.contatti ?? [])
    .filter((c) => !q || contiene(c.indirizzo, q) || contiene(c.nome, q)), [rubrica, q])
  const colleghi = useMemo(() => (rubrica?.colleghi ?? [])
    .filter((c) => !q || contiene(c.indirizzo, q) || contiene(c.nome, q)), [rubrica, q])

  if (errore && !caselle) return <Avviso tipo="errore">{errore}</Avviso>
  if (!caselle) return <Attesa testo="Caricamento…" />
  if (!caselle.length) return <Vuoto testo="Non hai caselle di posta da cui scrivere." />

  return (
    <>
      <p className="tenue">
        Gli indirizzi a cui si scrive da una casella e quante volte. La rubrica si aggiorna da sola a ogni
        invio e il compositore la propone mentre scrivi in A, Cc e Ccn, insieme ai colleghi di CIDIS.
      </p>
      <div className="rubrica-filtri">
        {caselle.length > 1 && (
          <Scelta etichetta="Casella" valore={casellaId} cambia={(v) => { setCasellaId(v); setMessaggio(null) }}
                  opzioni={caselle.map((c) => ({
                    valore: c.id,
                    etichetta: `${c.nome_visualizzato || c.indirizzo} <${c.indirizzo}>${c.mia ? '' : ' (sola lettura)'}`,
                  }))} />
        )}
        <Campo tipo="search" etichetta="Cerca" valore={cerca} cambia={setCerca} segnaposto="nome o indirizzo" />
      </div>
      {gestisce && (
        <ComandiFileRubrica casellaId={casellaId} esito={setErrore}
                            importato={(testo) => { setMessaggio(testo); carica() }} />
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
      {!rubrica && !errore && <Attesa testo="Caricamento…" />}
      {rubrica && (
        <>
          {!gestisce && (
            <p className="piccolo tenue">Su questa casella hai la sola lettura: la rubrica si consulta, non si corregge.</p>
          )}
          <Riquadro denso titolo={`Rubrica di ${casella?.indirizzo ?? ''}`}
                    azioni={gestisce && (
                      <Bottone dimensione="piccolo" onClick={() => setAggiungi(true)}>Aggiungi un contatto</Bottone>
                    )}>
            <Tabella intestazioni={['Nome', 'Indirizzo', 'Invii', 'Ultimo invio', '']}
                     vuoto={<Vuoto testo={q ? 'Nessun contatto trovato.' : 'La rubrica è vuota: si riempie al primo invio.'} />}>
              {contatti.map((c) => (
                <tr key={c.id}>
                  <td data-etichetta="Nome">{c.nome ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Indirizzo" className="mono">{c.indirizzo}</td>
                  <td data-etichetta="Invii">{c.invii}</td>
                  <td data-etichetta="Ultimo invio" className="piccolo">
                    {c.ultimo_invio_il ? giorno.format(new Date(c.ultimo_invio_il)) : '—'}
                    <div className="tenue">{ORIGINI[c.origine]}</div>
                  </td>
                  <td data-etichetta="" className="rubrica-azioni">
                    <Bottone dimensione="piccolo" variante="discreto" onClick={() => scrivi(casellaId, posta.voceDestinatario(c))}>Scrivi</Bottone>
                    {gestisce && (
                      <>
                        <Bottone dimensione="piccolo" variante="discreto" onClick={() => setCorreggi(c)}>Correggi il nome</Bottone>
                        <Bottone dimensione="piccolo" variante="discreto" onClick={() => setTogli(c)}>Togli</Bottone>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>
          <GruppiRubrica casellaId={casellaId} rubrica={rubrica} gestisce={gestisce} cerca={cerca} scrivi={scrivi}
                         aggiorna={(testo) => { setMessaggio(testo); carica() }} />
          <p className="piccolo tenue">
            Colleghi di CIDIS: le utenze attive del gestionale, con la casella personale. Sono sempre proposti nel
            compositore e non si correggono qui: chi entra o esce compare o sparisce da solo.
          </p>
          <Riquadro denso titolo="Colleghi di CIDIS">
            <Tabella intestazioni={['Nome', 'Indirizzo', '']}
                     vuoto={<Vuoto testo={q ? 'Nessun collega trovato.' : 'Nessun collega da mostrare.'} />}>
              {colleghi.map((c) => (
                <tr key={c.indirizzo}>
                  <td data-etichetta="Nome">{c.nome} <Etichetta>collega</Etichetta></td>
                  <td data-etichetta="Indirizzo" className="mono">{c.indirizzo}</td>
                  <td data-etichetta="" className="rubrica-azioni">
                    <Bottone dimensione="piccolo" variante="discreto"
                             onClick={() => scrivi(casellaId, posta.voceDestinatario(c))}>Scrivi</Bottone>
                  </td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>
        </>
      )}

      {aggiungi && (
        <ModuloContatto chiudi={() => setAggiungi(false)} salva={async (indirizzo, nome) => {
          await posta.aggiungiContatto(casellaId, indirizzo, nome)
          setAggiungi(false); setMessaggio(`${indirizzo} è nella rubrica.`); carica()
        }} />
      )}
      {correggi && (
        <ModuloContatto contatto={correggi} chiudi={() => setCorreggi(null)} salva={async (_, nome) => {
          await posta.correggiContatto(correggi.id, nome)
          setCorreggi(null); setMessaggio('Nome corretto.'); carica()
        }} />
      )}
      {togli && (
        <ConfermaTogli contatto={togli} chiudi={() => setTogli(null)} tolto={() => {
          setTogli(null); setMessaggio(`${togli.indirizzo} non è più nella rubrica.`); carica()
        }} />
      )}
    </>
  )
}

/** Nuovo contatto (indirizzo e nome) o correzione del nome di uno presente. */
function ModuloContatto({ contatto, chiudi, salva }: {
  contatto?: posta.Contatto
  chiudi: () => void
  salva: (indirizzo: string, nome: string | null) => Promise<void>
}) {
  const [indirizzo, setIndirizzo] = useState(contatto?.indirizzo ?? '')
  const [nome, setNome] = useState(contatto?.nome ?? '')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function conferma() {
    setInCorso(true); setErrore(null)
    try {
      await salva(indirizzo.trim(), nome.trim() || null)
    } catch (e) {
      setErrore(testoErrore(e))
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={contatto ? 'Correggi il nome' : 'Aggiungi un contatto'} chiudi={chiudi} azioni={(
      <>
        <Bottone onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!indirizzo.trim()} onClick={conferma}>Salva</Bottone>
      </>
    )}>
      {contatto
        ? <p className="mono">{contatto.indirizzo}</p>
        : <Campo tipo="email" etichetta="Indirizzo" valore={indirizzo} cambia={setIndirizzo} obbligatorio maxLength={254} />}
      <Campo etichetta="Nome" valore={nome} cambia={setNome} maxLength={200}
             aiuto="Come compare nei suggerimenti. Lasciato vuoto, resta il solo indirizzo." />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

function ConfermaTogli({ contatto, chiudi, tolto }: { contatto: posta.Contatto; chiudi: () => void; tolto: () => void }) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  return (
    <Modale titolo="Togli dalla rubrica" chiudi={chiudi} fuocoIniziale="azioni" azioni={(
      <>
        <Bottone onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="critico" inCorso={inCorso} onClick={() => {
          setInCorso(true)
          posta.cancellaContatto(contatto.id).then(tolto, (e) => { setErrore(testoErrore(e)); setInCorso(false) })
        }}>Togli</Bottone>
      </>
    )}>
      <p>
        <span className="mono">{contatto.indirizzo}</span> non sarà più proposto nel compositore. Se gli scrivi di
        nuovo da questa casella, torna nella rubrica.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
